import { useMemo, useState } from 'react';

import { Box, Col } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { corePdpLink } from 'components/helpers/links';
import { buildCarouselPhotos } from 'components/marketplace/helpers/galleryPhotos';
import {
  buildPropertyAnalyticsValue,
  PropertyAnalytics,
} from 'components/marketplace/helpers/useTrackProperty';
import { PhotoCarousel as MapPhotoCarousel } from 'components/marketplace/map/PhotoCarousel';
import {
  heroWalkthroughVideoUrl,
  miniPdpHeroVideo,
  pdpMediaAvailability,
} from 'components/marketplace/property/media/pdpEnhancedMedia';
import { usePdpMediaSlot } from 'components/marketplace/property/media/usePdpMediaSlot';
import { MiniMapPropertySummary } from 'components/marketplace/property/PropertySection/MiniMapPropertySummary';
import { ShareModal } from 'components/marketplace/property/ShareModal';
import type { MarketplaceProperty } from 'components/marketplace/property/types';

interface MiniPropertyDetailPageProps {
  property: MarketplaceProperty;
  onClose: () => void;
}

export const MobileMiniPropertyDetailPage = ({
  property,
  onClose,
}: MiniPropertyDetailPageProps) => {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const handleToggleShareModal = () => {
    setIsShareModalOpen((prev) => !prev);
  };

  const propertyLink = corePdpLink(property.slug, property.addressToken);

  const handleOnClick = () => {
    window.location.href = propertyLink;
  };

  // Floor plans intentionally omitted here: the PhotoCarousel cover-crops
  // portrait plans. Tapping a photo navigates to the full PDP, where floor
  // plans are rendered uncropped in the gallery and Lightbox.
  const carouselPhotos = buildCarouselPhotos(property);
  const analyticsValue = useMemo(
    () => buildPropertyAnalyticsValue(property, 'map-drawer'),
    [property],
  );
  const mediaAvailability = useMemo(
    () => pdpMediaAvailability(heroWalkthroughVideoUrl(property)),
    [property],
  );
  const mediaSlot = usePdpMediaSlot({
    addressToken: property.addressToken,
    availability: mediaAvailability,
    surface: 'map-drawer',
  });
  const heroVideo = miniPdpHeroVideo(property, mediaSlot);

  return (
    <PropertyAnalytics.Provider value={analyticsValue}>
      <Col className="h-full w-full" key={`mobile-mini-pdp-${property?.addressToken}`}>
        <Box className="shrink-0 pb-6">
          <MapPhotoCarousel
            key={`mobile-carousel-${property?.addressToken}`}
            photos={carouselPhotos}
            onClose={onClose}
            onOpenShareModal={handleToggleShareModal}
            isRounded
            onPhotoClick={handleOnClick}
            heroOverlay={heroVideo}
          />
        </Box>
        <Col
          data-mini-pdp-scroll
          className="min-h-0 flex-1 touch-pan-y gap-2.5 overflow-y-auto px-5"
        >
          <MiniMapPropertySummary property={property} />
          <Button
            analyticsName="mobile-map-pdp-link"
            href={propertyLink}
            variant="secondary"
            aria-label="View home"
            className="w-full"
          >
            View home
          </Button>
        </Col>
        <ShareModal
          property={property}
          isOpen={isShareModalOpen}
          onClose={handleToggleShareModal}
        />
      </Col>
    </PropertyAnalytics.Provider>
  );
};
